import React, { useEffect, useState } from 'react';
import { DNABox, DNAText } from '@alucio/lux-ui';
import { generateClient } from 'aws-amplify/api';
import type { VeevaVaultLoginOutput } from '@alucio/aws-beacon-amplify/src/API';
import { exchangeExternalOidcVaultCrmSsoToken } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { useHistory } from 'src/router';
import { ROUTES } from 'src/router/routeDef';
import { persistVeevaVaultSession } from 'src/state/machines/CRM/util';
import { useUserTenant } from '../../../state/redux/selector/user';
import {
  EXTERNAL_OIDC_VAULT_CRM_SSO_CODE_VERIFIER,
  EXTERNAL_OIDC_VAULT_CRM_SSO_STATE,
} from './constants';
import { getExternalOidcVaultCrmSsoConfig } from './config';
import {
  ExternalOidcVaultCrmSsoExchangeInput,
  SsoTemporaryStorage,
} from './types';

type CompleteExternalOidcVaultCrmSsoCallbackInput = {
  search: string,
  storage: SsoTemporaryStorage,
  redirectUri: string,
  exchangeToken: (input: ExternalOidcVaultCrmSsoExchangeInput) => Promise<VeevaVaultLoginOutput>,
}

const clearTemporaryStorage = (storage: SsoTemporaryStorage) => {
  storage.removeItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_STATE);
  storage.removeItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_CODE_VERIFIER);
};

export const completeExternalOidcVaultCrmSsoCallback = async ({
  search,
  storage,
  redirectUri,
  exchangeToken,
}: CompleteExternalOidcVaultCrmSsoCallbackInput): Promise<VeevaVaultLoginOutput> => {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const storedState = storage.getItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_STATE);
  const codeVerifier = storage.getItem?.(EXTERNAL_OIDC_VAULT_CRM_SSO_CODE_VERIFIER);

  if (!code) {
    throw new Error('The OIDC provider did not return an authorization code.');
  }

  if (!returnedState || returnedState !== storedState) {
    throw new Error('SSO state did not match the stored value.');
  }

  if (!codeVerifier) {
    throw new Error('PKCE code verifier was not found.');
  }

  try {
    return await exchangeToken({
      code,
      codeVerifier,
      redirectUri,
    });
  } finally {
    clearTemporaryStorage(storage);
  }
};

export const exchangeExternalOidcVaultCrmSsoTokenWithBackend = async (
  input: ExternalOidcVaultCrmSsoExchangeInput,
): Promise<VeevaVaultLoginOutput> => {
  const appsyncClient = generateClient();
  const response = await appsyncClient.graphql({
    query: exchangeExternalOidcVaultCrmSsoToken,
    variables: { input },
  }) as {
    data?: {
      exchangeExternalOidcVaultCrmSsoToken?: VeevaVaultLoginOutput,
    },
  };
  const session = response.data?.exchangeExternalOidcVaultCrmSsoToken;

  if (!session) {
    throw new Error('No Veeva Vault session was returned from the SSO exchange.');
  }

  return session;
};

const ExternalOidcVaultCrmSsoCallback: React.FC = () => {
  const [error, setError] = useState('');
  const history = useHistory();
  const userTenant = useUserTenant();

  useEffect(() => {
    if (typeof window === 'undefined' || !userTenant) {
      return undefined;
    }

    let isActive = true;
    let config: ReturnType<typeof getExternalOidcVaultCrmSsoConfig>;

    try {
      config = getExternalOidcVaultCrmSsoConfig(
        userTenant.config.crmIntegration?.externalOidcSso,
      );
    } catch (callbackError) {
      setError(callbackError instanceof Error ? callbackError.message : 'Unable to complete SSO exchange.');
      return undefined;
    }

    completeExternalOidcVaultCrmSsoCallback({
      search: window.location.search,
      storage: window.sessionStorage,
      redirectUri: config.redirectUri,
      exchangeToken: exchangeExternalOidcVaultCrmSsoTokenWithBackend,
    }).then((session) => {
      persistVeevaVaultSession(session);
      history.replace(ROUTES.HOME.PATH);
    }).catch((callbackError) => {
      if (isActive) {
        setError(callbackError instanceof Error ? callbackError.message : 'Unable to complete SSO exchange.');
      }
    });

    return () => {
      isActive = false;
    };
  }, [history, userTenant]);

  return (
    <DNABox fill alignX="center" alignY="center" style={{ padding: 24 }}>
      <DNAText>{error || 'Completing external OIDC SSO...'}</DNAText>
    </DNABox>
  );
};

export default ExternalOidcVaultCrmSsoCallback;
